import { ExportType, Frequency } from "PFApp/booking/types/export_booking";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useExportWizardModalContext } from "../export_wizard_modal_provider";
import { decamelizeFilters } from "./export_fields_utils";
import { convertFieldsResponse } from "./helpers";
import { ExportTab, FieldsTab, FiltersTab, TemplateAndReportTypeTab } from "./tabs";

export type TabType = "templateAndReportType" | "fields" | "filters" | "export";

type Tab = {
  id: TabType;
  displayElement: React.ReactNode;
};

type UseExportWizardTabs = {
  currentTab: TabType;
  tabs: Tab[];
  handleClickTab: (tab: Tab) => void;
  TabComponent: React.FC;
};

const TAB_COMPONENTS = {
  templateAndReportType: TemplateAndReportTypeTab,
  fields: FieldsTab,
  filters: FiltersTab,
  export: ExportTab
};

export const useExportWizardTabs = (): UseExportWizardTabs => {
  const {
    state: { data },
    updateData
  } = useExportWizardModalContext();
  const { currentTab = "templateAndReportType", selectedFields } = data || {};
  const { t } = useTranslation("bookingModule", { keyPrefix: "reports.exportWizard" });

  const tabs: Tab[] = useMemo(
    () => [
      { id: "templateAndReportType", displayElement: t("tabs.templateAndReportType") },
      { id: "fields", displayElement: t("tabs.fields") },
      { id: "filters", displayElement: t("tabs.filters"), disabled: !selectedFields?.length },
      { id: "export", displayElement: t("tabs.export"), disabled: !selectedFields?.length }
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedFields]
  );

  const TabComponent = TAB_COMPONENTS[currentTab];

  const handleClickTab = (value: Tab) => updateData({ currentTab: value.id });

  useEffect(() => {
    const { selectedTemplate } = data;
    if (selectedTemplate) {
      updateData({
        exportType: ExportType.Template,
        selectedFields: convertFieldsResponse(selectedTemplate.fields),
        selectedFilters: decamelizeFilters(selectedTemplate.filters),
        frequency: (selectedTemplate.aggregateBy || Frequency.Daily) as Frequency
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    currentTab,
    tabs,
    handleClickTab,
    TabComponent
  };
};
